/**
 * ApprovalHistory Component
 * Timeline view of approval actions for an OKR
 */

import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ClipboardList } from 'lucide-react';
import { Avatar, Badge, EmptyState, LoadingSpinner } from '../common';
import { useApprovalHistory } from '../../hooks';

export interface ApprovalHistoryProps {
  okrId: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
  createdByRole?: string;
}

export const ApprovalHistory: React.FC<ApprovalHistoryProps> = ({ okrId, createdBy, createdByRole }) => {
  const { data: approvals, isLoading, error } = useApprovalHistory(okrId);

  // Check if OKR was auto-approved (created by manager or account_admin)
  const isAutoApproved = createdByRole === 'manager' || createdByRole === 'account_admin';

  // Helper function to get badge variant based on action
  const getBadgeVariant = (action: string): 'success' | 'warning' | 'danger' | 'secondary' => {
    switch (action) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'submitted':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  // Helper function to format action text
  const getActionText = (action: string): string => {
    switch (action) {
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'submitted':
        return 'Submitted for Approval';
      default:
        return action;
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Approval History</h3>
        <LoadingSpinner size="md" text="Loading approval history..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">Approval History</h3>
        <p className="text-sm text-danger-600">Failed to load approval history</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">Approval History</h3>

      {!approvals || approvals.length === 0 ? (
        isAutoApproved ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">Auto Approved</h4>
            <p className="text-sm text-gray-500">
              This OKR was created by {createdBy?.firstName} {createdBy?.lastName} and did not require approval
            </p>
          </div>
        ) : (
          <EmptyState
            icon={ClipboardList}
            title="No approval history yet"
            description="Approval actions will appear here once the OKR goes through the approval workflow"
          />
        )
      ) : (
        <div className="space-y-6">
          {approvals.map((approval, index) => (
            <div key={approval.id} className="relative">
              {/* Timeline line */}
              {index !== approvals.length - 1 && (
                <div
                  className="absolute left-5 top-12 h-full w-0.5 bg-gray-200"
                  aria-hidden="true"
                />
              )}

              {/* Timeline item */}
              <div className="flex gap-4">
                {/* Avatar */}
                <div className="relative flex-shrink-0">
                  <Avatar
                    fallbackText={`${approval.reviewer.firstName} ${approval.reviewer.lastName}`}
                    size="md"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 space-y-2">
                  {/* Header: Name and Badge */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {approval.reviewer.firstName} {approval.reviewer.lastName}
                    </span>
                    <Badge variant={getBadgeVariant(approval.action)} size="sm">
                      {getActionText(approval.action)}
                    </Badge>
                  </div>

                  {/* Comments */}
                  {approval.comments && (
                    <p className="text-sm text-gray-700 rounded-md bg-gray-50 p-3">
                      {approval.comments}
                    </p>
                  )}

                  {/* Status Change */}
                  {approval.previousStatus && approval.newStatus && (
                    <p className="text-xs text-gray-500">
                      Status changed from{' '}
                      <span className="font-medium">{approval.previousStatus}</span> to{' '}
                      <span className="font-medium">{approval.newStatus}</span>
                    </p>
                  )}

                  {/* Timestamp */}
                  <time className="block text-xs text-gray-500">
                    {formatDistanceToNow(new Date(approval.createdAt), { addSuffix: true })}
                  </time>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
